/**
 * Room Designer — Tool Helpers Module
 * Extracted from custom-room-designer.js (pure refactor, zero behaviour change).
 *
 * Contains standalone helper functions used by the tool event handlers.
 * All functions are pure or near-pure: they take explicit parameters
 * and return values without depending on RoomDesigner instance state.
 *
 * Script load order: math → storage → tools → main → overlay
 *
 * SECTION MAP
 * ───────────────────────────────────────────
 * L17-22      Point key utility (_pointKey)
 * L24-72      Segment intersection helpers (segmentsIntersectPts, pointInPolygon, circle, rect)
 * L74-110     Graph/connectivity (getSegmentConnectedComponents)
 * L112-153    Wall topology (getWallOpenEndpoints, getWallsConnectedAt, getSegmentGroupEndpoints)
 * L155-236    Door constraints (doorsOverlap, getMinWallLengthForDoors, hasDoorAtPoint, recomputeAllDoorParams)
 * L238-250    Door swing options (getSwingOptions)
 * L252-310    Selection geometry (getOppositeHandle, getResizeHandles, getRotateHandle, currentGridStep)
 */
(function () {
  'use strict';

  var T = {};
  var M = window.RoomDesignerMath;

  /* ── POINT KEY UTILITY ── */

  T._pointKey = function (p) {
    return (Math.round(p.x * 1200) / 1200).toFixed(4) + ',' +
           (Math.round(p.y * 1200) / 1200).toFixed(4);
  };

  /* ── SEGMENT INTERSECTION HELPERS ── */

  /** Cross-product segment–segment intersection test. */
  T.segmentsIntersectPts = function (a1, a2, b1, b2) {
    var d1 = (b2.x - b1.x) * (a1.y - b1.y) - (b2.y - b1.y) * (a1.x - b1.x);
    var d2 = (b2.x - b1.x) * (a2.y - b1.y) - (b2.y - b1.y) * (a2.x - b1.x);
    var d3 = (a2.x - a1.x) * (b1.y - a1.y) - (a2.y - a1.y) * (b1.x - a1.x);
    var d4 = (a2.x - a1.x) * (b2.y - a1.y) - (a2.y - a1.y) * (b2.x - a1.x);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
    return false;
  };

  /** Ray-casting point-in-polygon test. */
  T.pointInPolygon = function (pt, polygon) {
    var inside = false;
    for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      var xi = polygon[i].x, yi = polygon[i].y;
      var xj = polygon[j].x, yj = polygon[j].y;
      if ((yi > pt.y) !== (yj > pt.y) &&
          pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };

  /** Segment–circle intersection via quadratic discriminant. */
  T.segmentIntersectsCircle = function (start, end, center, r) {
    var dx = end.x - start.x, dy = end.y - start.y;
    var fx = start.x - center.x, fy = start.y - center.y;
    var a = dx * dx + dy * dy;
    var b = 2 * (fx * dx + fy * dy);
    var c = fx * fx + fy * fy - r * r;
    var disc = b * b - 4 * a * c;
    if (disc < 0) return false;
    disc = Math.sqrt(disc);
    var t1 = (-b - disc) / (2 * a);
    var t2 = (-b + disc) / (2 * a);
    return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1) || (t1 < 0 && t2 > 1);
  };

  /** Segment–rectangle (convex polygon) intersection test. */
  T.segmentIntersectsRect = function (start, end, corners) {
    for (var i = 0; i < corners.length; i++) {
      var j = (i + 1) % corners.length;
      if (T.segmentsIntersectPts(start, end, corners[i], corners[j])) return true;
    }
    if (T.pointInPolygon(start, corners)) return true;
    return false;
  };

  /* ── GRAPH / CONNECTIVITY ── */

  /** Find connected components in a list of segments sharing endpoints. */
  T.getSegmentConnectedComponents = function (segments) {
    var key = T._pointKey;
    var visited = new Set();
    var adj = {};
    segments.forEach(function (seg, i) {
      var sk = key(seg.start), ek = key(seg.end);
      if (!adj[sk]) adj[sk] = [];
      if (!adj[ek]) adj[ek] = [];
      adj[sk].push(i);
      adj[ek].push(i);
    });
    var components = [];
    for (var i = 0; i < segments.length; i++) {
      if (visited.has(i)) continue;
      var comp = [];
      var stack = [i];
      while (stack.length > 0) {
        var idx = stack.pop();
        if (visited.has(idx)) continue;
        visited.add(idx);
        comp.push(segments[idx]);
        var seg = segments[idx];
        var keys = [key(seg.start), key(seg.end)];
        for (var ki = 0; ki < keys.length; ki++) {
          var neighbors = adj[keys[ki]] || [];
          for (var ni = 0; ni < neighbors.length; ni++) {
            if (!visited.has(neighbors[ni])) stack.push(neighbors[ni]);
          }
        }
      }
      components.push(comp);
    }
    return components;
  };

  /* ── WALL TOPOLOGY ── */

  /** Return which endpoints ('start'/'end') of `wall` are open (degree-1) in the room. */
  T.getWallOpenEndpoints = function (wall, room) {
    if (!room) return [];
    var wallList = room.segments || room.walls;
    var key = T._pointKey;
    var pointCount = {};
    wallList.forEach(function (w) {
      var sk = key(w.start), ek = key(w.end);
      pointCount[sk] = (pointCount[sk] || 0) + 1;
      pointCount[ek] = (pointCount[ek] || 0) + 1;
    });
    var result = [];
    if ((pointCount[key(wall.start)] || 0) === 1) result.push('start');
    if ((pointCount[key(wall.end)] || 0) === 1) result.push('end');
    return result;
  };

  /** Return walls in `room` that share an endpoint with `wall` at the given end. */
  T.getWallsConnectedAt = function (wall, room, whichEnd) {
    if (!room) return [];
    var wallList = room.segments || room.walls;
    var key = T._pointKey;
    var pt = whichEnd === 'start' ? wall.start : wall.end;
    var pk = key(pt);
    var connected = [];
    wallList.forEach(function (w) {
      if (w === wall) return;
      if (key(w.start) === pk || key(w.end) === pk) connected.push(w);
    });
    return connected;
  };

  /** Collect all segment endpoints from a group. */
  T.getSegmentGroupEndpoints = function (group) {
    var eps = [];
    for (var i = 0; i < group.segments.length; i++) {
      eps.push(group.segments[i].start, group.segments[i].end);
    }
    return eps;
  };

  /* ── DOOR CONSTRAINT HELPERS ── */

  /** Check whether a new door span [newMinParam, newMaxParam] overlaps existing doors on wall. */
  T.doorsOverlap = function (wall, room, newMinParam, newMaxParam, excludeDoor) {
    for (var i = 0; i < room.doors.length; i++) {
      var door = room.doors[i];
      if (door.wall !== wall || door === excludeDoor) continue;
      var dMin = Math.min(door.hingeParam, door.endParam);
      var dMax = Math.max(door.hingeParam, door.endParam);
      if (newMinParam < dMax - 0.001 && newMaxParam > dMin + 0.001) return true;
    }
    return false;
  };

  /** Sum of door widths (in feet) on a wall — the minimum wall length to fit all doors. */
  T.getMinWallLengthForDoors = function (wall, room) {
    var total = 0;
    for (var i = 0; i < room.doors.length; i++) {
      if (room.doors[i].wall === wall) total += room.doors[i].width / 12;
    }
    return total;
  };

  /** Check whether a room endpoint has a door hinge or end within 1 inch. */
  T.hasDoorAtPoint = function (room, point) {
    if (!room || !room.doors) return false;
    var threshold = 1 / 12;
    for (var i = 0; i < room.doors.length; i++) {
      var door = room.doors[i];
      var hingePoint = M.paramToPoint(door.wall, door.hingeParam);
      var endPoint = M.paramToPoint(door.wall, door.endParam);
      if (M.getDistance(point, hingePoint) < threshold ||
          M.getDistance(point, endPoint) < threshold) return true;
    }
    return false;
  };

  /**
   * Recompute door hingeParam/endParam after a wall's endpoints have moved.
   * Preserves door widths and pushes overlapping doors apart left-to-right.
   */
  T.recomputeAllDoorParamsFromSnapshots = function (doorSnapshots) {
    if (doorSnapshots.length === 0) return;
    var byWall = new Map();
    for (var si = 0; si < doorSnapshots.length; si++) {
      var ds = doorSnapshots[si];
      if (!byWall.has(ds.door.wall)) byWall.set(ds.door.wall, []);
      byWall.get(ds.door.wall).push(ds);
    }
    byWall.forEach(function (snapshots, wall) {
      var wdx = wall.end.x - wall.start.x, wdy = wall.end.y - wall.start.y;
      var lenSq = wdx * wdx + wdy * wdy;
      if (lenSq < 0.0001) return;
      var wallLen = Math.sqrt(lenSq);
      var doors = snapshots.map(function (ds) {
        var rawHinge = ((ds.hingePos.x - wall.start.x) * wdx + (ds.hingePos.y - wall.start.y) * wdy) / lenSq;
        var rawEnd = ((ds.endPos.x - wall.start.x) * wdx + (ds.endPos.y - wall.start.y) * wdy) / lenSq;
        var doorWidthParam = (ds.door.width / 12) / wallLen;
        return { ds: ds, rawMin: Math.min(rawHinge, rawEnd), doorWidthParam: doorWidthParam, hingeIsMin: rawHinge <= rawEnd };
      });
      doors.sort(function (a, b) { return a.rawMin - b.rawMin; });
      // Left-to-right: push doors right if they overlap previous or start before 0
      var cursor = 0;
      for (var i = 0; i < doors.length; i++) {
        doors[i].minP = Math.max(doors[i].rawMin, cursor, 0);
        doors[i].maxP = doors[i].minP + doors[i].doorWidthParam;
        cursor = doors[i].maxP;
      }
      // Right-to-left: push doors left if they extend past 1 or overlap next
      cursor = 1;
      for (var i = doors.length - 1; i >= 0; i--) {
        if (doors[i].maxP > cursor) { doors[i].maxP = cursor; doors[i].minP = cursor - doors[i].doorWidthParam; }
        cursor = doors[i].minP;
      }
      // Write back
      for (var i = 0; i < doors.length; i++) {
        var d = doors[i];
        if (d.hingeIsMin) { d.ds.door.hingeParam = d.minP; d.ds.door.endParam = d.maxP; }
        else { d.ds.door.hingeParam = d.maxP; d.ds.door.endParam = d.minP; }
      }
    });
  };

  /* ── DOOR SWING ── */

  /** Return the list of possible swing options for a door type. */
  T.getSwingOptions = function (doorType) {
    if (doorType === 'roller') return [];
    if (doorType === 'double') return [{ perpSign: 1 }, { perpSign: -1 }];
    return [
      { perpSign: 1, hingeEnd: 'hinge' },
      { perpSign: 1, hingeEnd: 'end' },
      { perpSign: -1, hingeEnd: 'hinge' },
      { perpSign: -1, hingeEnd: 'end' }
    ];
  };

  /* ── SELECTION GEOMETRY ── */

  /** Map handle name to its opposite. */
  T.getOppositeHandle = function (name) {
    var map = { nw: 'se', n: 's', ne: 'sw', e: 'w', se: 'nw', s: 'n', sw: 'ne', w: 'e' };
    return map[name] || 's';
  };

  /** Compute resize handle positions for an object. */
  T.getResizeHandles = function (obj, isMobile) {
    if (!obj) return [];
    if (obj.type === 'equipment') return []; // Equipment is to-scale, no resize
    if (obj.type === 'rectangle' || obj.type === 'turf') {
      var hw = obj.width / 2, hd = obj.depth / 2;
      var rot = obj.rotation || 0;
      var allLocals = [
        { name: 'nw', lx: -hw, ly: -hd },
        { name: 'n',  lx: 0,   ly: -hd },
        { name: 'ne', lx: hw,  ly: -hd },
        { name: 'e',  lx: hw,  ly: 0   },
        { name: 'se', lx: hw,  ly: hd  },
        { name: 's',  lx: 0,   ly: hd  },
        { name: 'sw', lx: -hw, ly: hd  },
        { name: 'w',  lx: -hw, ly: 0   }
      ];
      var locals = isMobile
        ? allLocals.filter(function (h) { return ['nw','ne','se','sw'].indexOf(h.name) >= 0; })
        : allLocals;
      return locals.map(function (h) {
        var p = M._rotatePoint(obj.x + h.lx, obj.y + h.ly, obj.x, obj.y, rot);
        return { name: h.name, wx: p.x, wy: p.y };
      });
    }
    if (obj.type === 'circle') {
      var r = obj.diameter / 2;
      return [
        { name: 'n',  wx: obj.x,     wy: obj.y - r },
        { name: 'e',  wx: obj.x + r, wy: obj.y     },
        { name: 's',  wx: obj.x,     wy: obj.y + r },
        { name: 'w',  wx: obj.x - r, wy: obj.y     }
      ];
    }
    return [];
  };

  /** Compute rotate handle position for a rectangle object. */
  T.getRotateHandle = function (obj, scale, mouseWorld) {
    if (!obj) return null;
    var stemWorld = 20 / scale;
    if (obj.type === 'rectangle' || obj.type === 'equipment' || obj.type === 'turf' || obj.type === 'platform' || obj.type === 'label') {
      // Use tight bounds for equipment so handle hugs visual content
      var tw = (obj.type === 'equipment' && obj.tightWidth) ? obj.tightWidth : obj.width;
      var td = (obj.type === 'equipment' && obj.tightDepth) ? obj.tightDepth : obj.depth;
      var tox = (obj.type === 'equipment' ? (obj.tightOffsetX || 0) : 0);
      var toy = (obj.type === 'equipment' ? (obj.tightOffsetY || 0) : 0);
      var hw = tw / 2, hd = td / 2;
      var rot = obj.rotation || 0;
      // Rotate tight center offset into world space
      var cosR = Math.cos(rot), sinR = Math.sin(rot);
      var tcx = obj.x + tox * cosR - toy * sinR;
      var tcy = obj.y + tox * sinR + toy * cosR;
      // Visual half-height of the rotated bounding box — clears the top edge at any angle
      var visualHalfH = Math.abs(cosR) * hd + Math.abs(sinR) * hw;
      var circumR = visualHalfH + stemWorld;
      if (mouseWorld) {
        // During active rotation: orbit in a perfect circle following mouse angle
        var dx = mouseWorld.x - tcx, dy = mouseWorld.y - tcy;
        var angle = Math.atan2(dy, dx);
        return { wx: tcx + Math.cos(angle) * circumR, wy: tcy + Math.sin(angle) * circumR };
      }
      // At rest: visually above (north)
      return { wx: tcx, wy: tcy - circumR };
    } else if (obj.type === 'circle') {
      var r = obj.diameter / 2 + stemWorld;
      if (mouseWorld) {
        var cdx = mouseWorld.x - obj.x, cdy = mouseWorld.y - obj.y;
        var cAngle = Math.atan2(cdy, cdx);
        return { wx: obj.x + Math.cos(cAngle) * r, wy: obj.y + Math.sin(cAngle) * r };
      }
      return { wx: obj.x, wy: obj.y - r };
    }
    return null;
  };

  /** Grid step size based on zoom level. Imperial: 1" fine / 1' coarse. Metric: 1cm fine / 0.25m coarse. */
  T.currentGridStep = function (basePixelsPerFoot, zoomLevel, metric) {
    var spi = basePixelsPerFoot * zoomLevel / 12;
    var fine = spi >= 3;
    return metric
      ? (fine ? 0.032808399 : 0.82020997)
      : (fine ? 0.083333333 : 1);
  };

  window.RoomDesignerTools = T;
})();

